import React, { useCallback } from 'react';

// Styles & Assets
import styles from './PreconfigErrorDialog.module.css';
import { ReactComponent as IconInfo } from '../../../assets/icons/Icon-info.svg';

// Hooks
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../hooks/useTranslate';

// Interfaces
import { Parameter } from '../../../interfaces/Profile';
import { Poll } from '../../../interfaces/Poll';
import { PreconfigErrors } from '../../../hooks/preconfig-hooks';

interface Props {
    errors: PreconfigErrors
    sheetParameters: Parameter[]
    inquiries: Poll[]
    closeModal: () => void
}

const PreconfigErrorDialog: React.FC<Props> = ({
    errors,
    sheetParameters,
    inquiries,
    closeModal,
}) => {
    const { t } = useTranslation();
    const { translateMiddleware } = useTranslate();

    const getParameterName = useCallback((parameter: Parameter | undefined) => {
        if (!parameter) return '';
        return translateMiddleware(parameter.name_translations)?.text ?? parameter.name ?? parameter.id ?? '';
    }, [translateMiddleware]);

    const getInquiryName = useCallback((inquiry: Poll | undefined) => {
        if (!inquiry) return '';
        return inquiry.name ?? inquiry.id ?? '';
    }, []);

    const getListOfParameters = useCallback((sheetParameterIds: string[]) => {
        return sheetParameterIds
            .map(sheetParamId => {
                return getParameterName(
                    sheetParameters.find(s => String(s.id) === sheetParamId)
                );
            })
            .filter(Boolean)
            .join(', ');
    }, [getParameterName, sheetParameters]);

    const getListOfInquiries = useCallback((inquiryIds: string[]) => {
        return inquiryIds
            .map(inquiryId => {
                return getInquiryName(
                    inquiries.find(i => String(i.id) === inquiryId)
                );
            })
            .filter(Boolean)
            .join(', ');
    }, [getInquiryName, inquiries]);

    const splitFrequencyErrorKeys = useCallback((keys: string[]) => {
        const parameterKeys: string[] = [];
        const inquiryKeys: string[] = [];

        keys.forEach(key => {
            const isParameter = sheetParameters.some(param => String(param.id) === key);
            const isInquiry = inquiries.some(inquiry => String(inquiry.id) === key);

            if (isParameter) {
                parameterKeys.push(key);
            } else if (isInquiry) {
                inquiryKeys.push(key);
            }
        });

        return { parameterKeys, inquiryKeys };
    }, [sheetParameters, inquiries]);

    const frequencyErrorKeys = errors.FREQUENCY_VALUE_REQUIRED
        ? Object.keys(errors.FREQUENCY_VALUE_REQUIRED)
        : [];

    const { parameterKeys: frequencyParameterKeys, inquiryKeys: frequencyInquiryKeys } =
        splitFrequencyErrorKeys(frequencyErrorKeys);

    const frequencyParameterList = getListOfParameters(frequencyParameterKeys);
    const frequencyInquiryList = getListOfInquiries(frequencyInquiryKeys);

    return (
        <div className='dialog'>
            <h1 className={`dialogTitle ${styles.flexBar}`}>
                <IconInfo style={{ color: '#f00' }} />
                {t('error')}
            </h1>

            <p>
                {t('to_save_changes_preconfig')}
            </p>

            <ul className={styles.errorList}>
                {errors.NAME_REQUIRED && (
                    <li>
                        {t('preconfig_name_required')}
                    </li>
                )}

                {errors.PROFILE_REQUIRED && (
                    <li>
                        {t('preconfig_profile_required')}
                    </li>
                )}

                {errors.PARAMETER_EMPTY && (
                    <li>
                        {t('parameter_empty')} {getListOfParameters(Object.keys(errors.PARAMETER_EMPTY))}
                    </li>
                )}

                {errors.PARAMETER_LIMITS_INVALID && (
                    <li>
                        {t('parameter_limits_invalid')} {getListOfParameters(Object.keys(errors.PARAMETER_LIMITS_INVALID))}
                    </li>
                )}

                {errors.FREQUENCY_VALUE_REQUIRED && frequencyParameterList && (
                    <li>
                        {t('frequency_value_required_parameters')} {frequencyParameterList}
                    </li>
                )}

                {errors.FREQUENCY_VALUE_REQUIRED && frequencyInquiryList && (
                    <li>
                        {t('frequency_value_required_inquiries')} {frequencyInquiryList}
                    </li>
                )}
            </ul>

            <div style={{ textAlign: 'center' }}>
                <button className='button btnPrimary' onClick={closeModal}>
                    {t('close')}
                </button>
            </div>
        </div>
    );
};

export default PreconfigErrorDialog;